import { type ComponentProps, useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "@/shared/lib";

import { SidebarContext, type SidebarState } from "./SidebarContext";

const STORAGE_KEY = "relock.sidebar";
// Брейкпоинт `md` Tailwind: шире — меню слева, уже — шторка.
// eslint-disable-next-line lingui/no-unlocalized-strings -- медиазапрос, а не текст
const DESKTOP_QUERY = "(min-width: 48rem)";

// Хранилище браузера может быть недоступно (приватный режим, запрет cookies) — тогда меню
// просто не запоминает, что его свернули.
const readState = (): SidebarState => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "collapsed" ? "collapsed" : "expanded";
  } catch {
    return "expanded";
  }
};

const saveState = (state: SidebarState) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // Не запомнили — при следующем открытии меню снова развёрнуто.
  }
};

// Состояние бокового меню и раскладка «меню слева — страница справа». Свёрнутое меню
// запоминается в браузере; шторка на телефоне закрывается, если окно стало широким.
export const SidebarProvider = ({ className, children, ...props }: ComponentProps<"div">) => {
  const [state, setState] = useState<SidebarState>(readState);
  const [openMobile, setOpenMobile] = useState(false);

  const toggle = useCallback(() => {
    const next = state === "expanded" ? "collapsed" : "expanded";
    setState(next);
    saveState(next);
  }, [state]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia(DESKTOP_QUERY);
    const closeOnDesktop = () => {
      if (query.matches) {
        setOpenMobile(false);
      }
    };
    query.addEventListener("change", closeOnDesktop);
    return () => {
      query.removeEventListener("change", closeOnDesktop);
    };
  }, []);

  const value = useMemo(
    () => ({ state, toggle, openMobile, setOpenMobile }),
    [state, toggle, openMobile],
  );

  return (
    <SidebarContext.Provider value={value}>
      <div data-slot="sidebar-wrapper" className={cn("flex min-h-dvh", className)} {...props}>
        {children}
      </div>
    </SidebarContext.Provider>
  );
};
